import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to trap focus within a modal or dialog
 * Implements WCAG 2.1 focus management requirements
 *
 * @param {boolean} isActive - Whether the focus trap is active
 * @param {Object} options - Configuration options
 * @param {boolean} options.restoreFocus - Whether to restore focus when trap is deactivated (default: true)
 * @param {boolean} options.autoFocus - Whether to auto-focus the first focusable element (default: true)
 * @returns {React.RefObject} - Ref to attach to the container element
 */
export function useFocusTrap(isActive, options = {}) {
  const { restoreFocus = true, autoFocus = true } = options;
  const containerRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    return Array.from(containerRef.current.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Filter out hidden elements
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift + Tab
    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }, [getFocusableElements]);

  // Handle Escape key to close modal
  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape') {
      // Let the parent component handle closing
      // This is just for awareness - the actual close should be handled by the modal
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // Store currently focused element
    previousActiveElement.current = document.activeElement;

    // Auto-focus first focusable element
    if (autoFocus) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      });
    }

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleEscape);

      // Restore focus when modal closes
      if (restoreFocus && previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isActive, autoFocus, restoreFocus, getFocusableElements, handleKeyDown, handleEscape]);

  return containerRef;
}

export default useFocusTrap;
